import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOrg } from '@/contexts/OrgContext';
import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { useTitle } from '@/hooks/useTitle';
import { Select, type SelectItemType } from '@/lib/base/forms/select/select';
import { CRMObjectListPage, emailThreadListConfig } from '@/lib/crm/list';
import { getGravatarUrl } from '@/lib/crm/utils/avatar';

const USER_ASSOCIATION = 'ahoy_email_thread_to_user' as const;

// Matches the Avatar 'xs' size used by SelectItem (24px) at 2× for retina.
const GRAVATAR_PIXEL_SIZE = 48;

export default function EmailThreadsPage() {
  useTitle('Email Threads');
  const { currentOrg } = useOrg();
  const { entityId: currentUserEntityId } = useCurrentUserEntity();
  const { members, isLoading: membersLoading } = useOrgMembers(currentOrg?.id);

  const [searchParams, setSearchParams] = useSearchParams();
  const userParam = searchParams.get('user');

  const selectedUserId = useMemo(() => {
    if (userParam) {
      const found = members.find(m => m.entityId === userParam);
      if (found) {
        return found.entityId;
      }
    }
    return currentUserEntityId;
  }, [userParam, members, currentUserEntityId]);

  useEffect(() => {
    if (membersLoading) {
      return;
    }
    if (userParam && selectedUserId && userParam !== selectedUserId) {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('user', selectedUserId);
          return next;
        },
        { replace: true }
      );
    }
  }, [membersLoading, userParam, selectedUserId, setSearchParams]);

  const handleUserChange = useCallback(
    (entityId: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('user', entityId);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSelectionChange = useCallback(
    (key: string | number | null) => {
      if (key === null || key === undefined) {
        return;
      }
      handleUserChange(String(key));
    },
    [handleUserChange]
  );

  const config = useMemo(
    () => ({
      ...emailThreadListConfig,
      defaultFilters: {
        ...emailThreadListConfig.defaultFilters,
        filter_groups: selectedUserId
          ? [
              {
                filters: [
                  {
                    association: USER_ASSOCIATION,
                    operator: 'exact' as const,
                    value: selectedUserId,
                  },
                ],
              },
            ]
          : undefined,
      },
    }),
    [selectedUserId]
  );

  const selectedMember = useMemo(
    () => members.find(m => m.entityId === selectedUserId),
    [members, selectedUserId]
  );
  const isCurrentUser = selectedUserId === currentUserEntityId;
  const countLabel = isCurrentUser
    ? 'threads for you'
    : `threads for ${selectedMember?.displayName ?? 'unknown'}`;
  const countLabelSingular = isCurrentUser
    ? 'thread for you'
    : `thread for ${selectedMember?.displayName ?? 'unknown'}`;

  // Pin the current user first, then sort the rest alphabetically by display name.
  const sortedMembers = useMemo(() => {
    const current = members.filter(m => m.entityId === currentUserEntityId);
    const rest = members
      .filter(m => m.entityId !== currentUserEntityId)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    return [...current, ...rest];
  }, [members, currentUserEntityId]);

  // Build SelectItemType entries so Select's built-in rendering handles
  // avatar + name + supporting text (matches design-system pattern used
  // elsewhere — e.g. TimezoneInput, SelectInput).
  const items = useMemo<SelectItemType[]>(
    () =>
      sortedMembers.map(m => ({
        id: m.entityId,
        label: m.displayName,
        avatarUrl: m.email
          ? getGravatarUrl(m.email, GRAVATAR_PIXEL_SIZE)
          : undefined,
        supportingText: m.entityId === currentUserEntityId ? 'You' : undefined,
      })),
    [sortedMembers, currentUserEntityId]
  );

  const userDropdown = useMemo(
    () => (
      <div className='w-64'>
        <Select
          aria-label='Filter by team member'
          size='sm'
          items={items}
          selectedKey={selectedUserId ?? null}
          onSelectionChange={handleSelectionChange}
          isDisabled={items.length === 0}
          placeholder='Select team member'
        >
          {(item: SelectItemType) => (
            <Select.Item
              id={item.id}
              label={item.label}
              avatarUrl={item.avatarUrl}
              supportingText={item.supportingText}
            />
          )}
        </Select>
      </div>
    ),
    [items, selectedUserId, handleSelectionChange]
  );

  return (
    <CRMObjectListPage
      key={selectedUserId ?? 'default'}
      config={config}
      toolbarLeading={userDropdown}
      countLabel={countLabel}
      countLabelSingular={countLabelSingular}
    />
  );
}
